<template>

    <div class="mb-page">

        <!-- 1. En-tête + filtres -->
        <div class="ds-page-head">
            <div>
                <h1 class="ds-page-head__title">Marges bénéficiaires</h1>
                <p class="ds-page-head__sub">
                    Du {{ formatDate(dateDebut) }} au {{ formatDate(dateFin) }}
                    <template v-if="charge"> · {{ formatNombre(lignes.length) }} lignes de vente</template>
                    <template v-if="derniereMaj"> · mis à jour à {{ derniereMaj }}</template>
                </p>
            </div>
            <div class="mb-filters">
                <div class="mb-seg" role="group" aria-label="Période">
                    <button v-for="p in presets" :key="p.code" type="button"
                            :class="{ 'mb-seg--on': preset === p.code }"
                            @click="choisirPreset(p.code)">{{ p.label }}</button>
                </div>
                <template v-if="preset === 'perso'">
                    <input type="date" class="mb-input" v-model="dateDebut" :max="dateFin" @change="charger">
                    <input type="date" class="mb-input" v-model="dateFin" :min="dateDebut" @change="charger">
                </template>
                <select class="mb-input" v-model="magasin" aria-label="Magasin" @change="page = 1">
                    <option value="">Tous les magasins</option>
                    <option v-for="m in magasins" :key="m" :value="m">{{ m }}</option>
                </select>
                <button type="button" class="mb-btn" title="Actualiser" :disabled="chargement" @click="charger(true)">
                    <i class="ri-refresh-line" :class="{ 'mb-spin': chargement }"></i>
                </button>
                <button type="button" class="mb-btn mb-btn--xls" title="Exporter la sélection" :disabled="!lignesFiltrees.length" @click="exporterExcel">
                    <i class="ri-file-excel-2-line"></i> Excel
                </button>
            </div>
        </div>

        <!-- Données servies par le cache 5 min -->
        <bandeaucachetb v-if="depuisCache" quoi="les marges de la période" verbe="sont gardées"
                        manque="Les ventes et les retours faits depuis n'y figurent pas encore."
                        :heure="heureCache" @actualiser="charger(true)"></bandeaucachetb>

        <!-- 2. KPI -->
        <div class="mb-kpis">
            <template v-if="charge">
                <div class="mb-kpi mb-kpi--main">
                    <div class="mb-kpi__lbl">Bénéfice brut <i class="ri-funds-line"></i></div>
                    <div class="mb-kpi__val">{{ formatMoney(totaux.bn) }} <small>F CFA</small></div>
                    <div class="mb-kpi__sub">{{ magasin ? magasin : 'Tous les magasins' }}</div>
                </div>
                <div class="mb-kpi">
                    <div class="mb-kpi__lbl">Chiffre d'affaires <i class="ri-shopping-bag-3-line mb-ico-vente"></i></div>
                    <div class="mb-kpi__val">{{ formatMoney(totaux.mv) }} <small>F</small></div>
                    <div class="mb-kpi__sub">{{ formatNombre(totaux.q) }} articles vendus, remises déduites</div>
                </div>
                <div class="mb-kpi">
                    <div class="mb-kpi__lbl">Coût d'achat <i class="ri-truck-line mb-ico-achat"></i></div>
                    <div class="mb-kpi__val">{{ formatMoney(totaux.ma) }} <small>F</small></div>
                    <div class="mb-kpi__sub">{{ totaux.mv ? Math.round(totaux.ma / totaux.mv * 100) : 0 }} % du chiffre d'affaires</div>
                </div>
                <div class="mb-kpi">
                    <div class="mb-kpi__lbl">Marge réelle <i class="ri-percent-line mb-ico-marge"></i></div>
                    <div class="mb-kpi__val">{{ formatPourcent(totaux.marge) }} <small>%</small></div>
                    <div class="mb-gauge"><i :style="{ width: Math.max(0, Math.min(100, totaux.marge)) + '%' }"></i></div>
                </div>
                <div class="mb-kpi mb-kpi--warn" role="button" tabindex="0"
                     @click="choisirPastille('sanspa')" @keyup.enter="choisirPastille('sanspa')">
                    <div class="mb-kpi__lbl">Sans prix d'achat <i class="ri-error-warning-line mb-ico-warn"></i></div>
                    <div class="mb-kpi__val">{{ totaux.sanspa }} <small>article{{ totaux.sanspa > 1 ? 's' : '' }}</small></div>
                    <div class="mb-kpi__sub">{{ formatMoney(totaux.ventesSansPa) }} F de ventes comptées à 100 % de marge. <b class="mb-warn">Voir</b></div>
                </div>
            </template>
            <template v-else>
                <div v-for="n in 5" :key="n" class="mb-kpi">
                    <div class="mb-skel" style="width:45%"></div>
                    <div class="mb-skel mb-skel--lg" style="width:70%"></div>
                    <div class="mb-skel" style="width:85%"></div>
                </div>
            </template>
        </div>

        <!-- 3. Vues -->
        <div class="mb-views">
            <button type="button" :class="{ 'mb-views--on': vue === 'table' }" @click="vue = 'table'"><i class="ri-table-line"></i> Achats et ventes</button>
            <button type="button" :class="{ 'mb-views--on': vue === 'chart' }" @click="vue = 'chart'"><i class="ri-bar-chart-box-line"></i> Graphiques</button>
        </div>

        <!-- 4. Vue tableau -->
        <div v-show="vue === 'table'" class="mb-card">
            <div class="mb-toolbar">
                <div class="mb-search">
                    <i class="ri-search-line"></i>
                    <input ref="recherche" type="search" v-model="recherche" placeholder="Rechercher un article, un magasin..."
                           @keyup.esc="recherche = ''">
                    <kbd v-if="!recherche">/</kbd>
                    <button v-else type="button" class="mb-search__clear" title="Effacer" @click="recherche = ''; $refs.recherche.focus()"><i class="ri-close-line"></i></button>
                </div>
                <div class="mb-seg" role="group" aria-label="Regroupement">
                    <button v-for="g in regroupements" :key="g.code" type="button"
                            :class="{ 'mb-seg--on': regroupement === g.code }"
                            @click="regroupement = g.code; page = 1">{{ g.label }}</button>
                </div>
                <select class="mb-input" v-model.number="parPage" aria-label="Lignes par page" @change="page = 1">
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                </select>
            </div>

            <div class="mb-chips">
                <button v-for="c in pastilles" :key="c.code" type="button" class="mb-chip"
                        :class="[c.cls, { 'mb-chip--on': pastille === c.code }]"
                        @click="choisirPastille(c.code)">
                    {{ c.label }} <b>{{ compteurs[c.code] }}</b>
                </button>
                <span class="mb-chips__sep"></span>
                <span class="mb-resume">
                    {{ lignesFiltrees.length }} résultat{{ lignesFiltrees.length > 1 ? 's' : '' }}
                    <template v-if="recherche"> pour « {{ recherche }} »</template>
                </span>
            </div>

            <template v-if="charge">
                <!-- tableau (écran large) -->
                <div class="mb-table-wrap mb-only-desktop">
                    <table>
                        <thead>
                        <tr>
                            <th v-for="c in colonnes" :key="c.k" class="th"
                                :class="[{ 'th--right': c.r, 'th--sorted': tri === c.k }, c.g ? 'th--' + c.g : '']"
                                @click="trier(c.k)">
                                {{ c.l }}<span class="th__s">{{ tri === c.k ? (sens < 0 ? ' ↓' : ' ↑') : ' ↕' }}</span>
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="!lignesFiltrees.length">
                            <td :colspan="colonnes.length">
                                <div class="mb-empty">
                                    <i class="ri-search-eye-line"></i>
                                    <b>Aucun article ne correspond.</b><br>
                                    Changez la recherche, la pastille ou la période.
                                </div>
                            </td>
                        </tr>
                        <tr v-for="g in lignesPage" :key="g.cle" class="mb-row">
                            <td class="td">
                                <div class="mb-art">
                                    <span class="mb-avatar" :style="{ background: couleur(g.art) }">{{ initiales(g.art) }}</span>
                                    <div>
                                        <div class="mb-art__name">
                                            <span v-html="surligner(g.art)"></span>
                                            <span v-if="g.sanspa" class="mb-tag mb-tag--pa">PA manquant</span>
                                            <span v-if="g.br" class="mb-tag mb-tag--ret">{{ g.br }} retour{{ g.br > 1 ? 's' : '' }}</span>
                                        </div>
                                        <div class="mb-art__meta" v-html="surligner(meta(g))"></div>
                                    </div>
                                </div>
                            </td>
                            <td class="td td--right">{{ formatNombre(g.q) }}</td>
                            <td class="td td--right mb-ca"><span :class="{ 'mb-faint': g.sanspa }">{{ formatMoney(g.pa) }}</span></td>
                            <td class="td td--right mb-ca"><b>{{ formatMoney(g.ma) }}</b></td>
                            <td class="td td--right mb-cv">{{ formatMoney(g.pu) }}</td>
                            <td class="td td--right mb-cv">
                                <b>{{ formatMoney(g.mv) }}</b>
                                <div v-if="g.rem" class="mb-art__meta">remise {{ formatMoney(g.rem) }}</div>
                            </td>
                            <td class="td td--right" :class="g.bn < 0 ? 'mb-neg' : 'mb-pos'">{{ formatMoney(g.bn) }}</td>
                            <td class="td td--right">
                                <span class="mb-mbar">
                                    <b :style="{ color: couleurMarge(g.marge) }">{{ formatPourcent(g.marge) }} %</b>
                                    <span><i :style="{ width: Math.max(3, Math.min(100, Math.abs(g.marge))) + '%', background: couleurMarge(g.marge) }"></i></span>
                                </span>
                            </td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                            <td class="td"><span class="mb-foot__lbl">Total de la sélection</span>{{ lignesFiltrees.length }} ligne{{ lignesFiltrees.length > 1 ? 's' : '' }}</td>
                            <td class="td td--right"><span class="mb-foot__lbl">Qté</span>{{ formatNombre(totauxSelection.q) }}</td>
                            <td class="td"></td>
                            <td class="td td--right"><span class="mb-foot__lbl">Montant achat</span>{{ formatMoney(totauxSelection.ma) }}</td>
                            <td class="td"></td>
                            <td class="td td--right"><span class="mb-foot__lbl">Montant vente</span>{{ formatMoney(totauxSelection.mv) }}</td>
                            <td class="td td--right"><span class="mb-foot__lbl">Bénéfice</span>{{ formatMoney(totauxSelection.bn) }}</td>
                            <td class="td td--right"><span class="mb-foot__lbl">Marge</span>{{ formatPourcent(totauxSelection.marge) }} %</td>
                        </tr>
                        </tfoot>
                    </table>
                </div>

                <!-- cartes (mobile) -->
                <div class="mb-mcards mb-only-mobile">
                    <div v-if="!lignesFiltrees.length" class="mb-empty"><i class="ri-search-eye-line"></i>Aucun article ne correspond.</div>
                    <div v-for="g in lignesPage" :key="g.cle" class="mb-mcard">
                        <div class="mb-art">
                            <span class="mb-avatar" :style="{ background: couleur(g.art) }">{{ initiales(g.art) }}</span>
                            <div>
                                <div class="mb-art__name">
                                    <span v-html="surligner(g.art)"></span>
                                    <span v-if="g.sanspa" class="mb-tag mb-tag--pa">PA manquant</span>
                                </div>
                                <div class="mb-art__meta">Qté {{ formatNombre(g.q) }} · marge <b :style="{ color: couleurMarge(g.marge) }">{{ formatPourcent(g.marge) }} %</b></div>
                            </div>
                        </div>
                        <div class="mb-mcard__g">
                            <div>Prix achat<b>{{ formatMoney(g.pa) }}</b></div>
                            <div>Prix vente<b>{{ formatMoney(g.pu) }}</b></div>
                            <div>Montant achat<b>{{ formatMoney(g.ma) }}</b></div>
                            <div>Montant vente<b>{{ formatMoney(g.mv) }}</b></div>
                            <div>Bénéfice<b :class="g.bn < 0 ? 'mb-neg' : 'mb-pos'">{{ formatMoney(g.bn) }}</b></div>
                        </div>
                    </div>
                    <div v-if="lignesFiltrees.length" class="mb-mtot">
                        <div>Montant achat<b>{{ formatMoney(totauxSelection.ma) }}</b></div>
                        <div>Montant vente<b>{{ formatMoney(totauxSelection.mv) }}</b></div>
                        <div>Bénéfice<b>{{ formatMoney(totauxSelection.bn) }}</b></div>
                        <div>Marge<b>{{ formatPourcent(totauxSelection.marge) }} %</b></div>
                    </div>
                </div>

                <!-- pagination -->
                <div class="mb-pager">
                    <span v-if="lignesFiltrees.length">{{ (page - 1) * parPage + 1 }} à {{ Math.min(page * parPage, lignesFiltrees.length) }} sur {{ lignesFiltrees.length }}</span>
                    <span v-else>0 résultat</span>
                    <span class="mb-pager__pg">
                        <button type="button" :disabled="page <= 1" @click="page--">‹</button>
                        <button v-for="p in pagesVisibles" :key="p" type="button" :class="{ 'mb-pager--on': p === page }" @click="page = p">{{ p }}</button>
                        <button type="button" :disabled="page >= nbPages" @click="page++">›</button>
                    </span>
                </div>
            </template>
            <div v-else class="mb-card__body">
                <div v-for="n in 8" :key="n" class="mb-skel"></div>
            </div>

            <!-- table d'export (cachée) : toute la sélection, chiffres bruts -->
            <table ref="exportable_table" class="mb-hidden">
                <thead>
                <tr><th>Article</th><th>Magasin</th><th v-if="regroupement === 'jour'">Date</th><th>Qté</th><th>Prix achat</th><th>Montant achat</th><th>Prix vente</th><th>Remise</th><th>Montant vente</th><th>Bénéfice</th><th>Marge %</th></tr>
                </thead>
                <tbody>
                <tr v-for="g in lignesFiltrees" :key="'x' + g.cle">
                    <td>{{ g.art }}</td><td>{{ g.mag || Object.keys(g.mags).join(', ') }}</td><td v-if="regroupement === 'jour'">{{ formatDate(g.j) }}</td>
                    <td>{{ g.q }}</td><td>{{ arrondi(g.pa) }}</td><td>{{ arrondi(g.ma) }}</td><td>{{ arrondi(g.pu) }}</td><td>{{ arrondi(g.rem) }}</td><td>{{ arrondi(g.mv) }}</td><td>{{ arrondi(g.bn) }}</td><td>{{ arrondi(g.marge) }}</td>
                </tr>
                </tbody>
                <tfoot>
                <tr><td>TOTAL</td><td></td><td v-if="regroupement === 'jour'"></td><td>{{ totauxSelection.q }}</td><td></td><td>{{ arrondi(totauxSelection.ma) }}</td><td></td><td></td><td>{{ arrondi(totauxSelection.mv) }}</td><td>{{ arrondi(totauxSelection.bn) }}</td><td>{{ arrondi(totauxSelection.marge) }}</td></tr>
                </tfoot>
            </table>
        </div>

        <!-- 5. Vue graphiques -->
        <div v-if="vue === 'chart'" class="mb-charts">
            <div class="mb-card">
                <div class="mb-card__head"><h2>Ventes, achats et bénéfice</h2><span>par jour</span></div>
                <div class="mb-card__body">
                    <apexchart v-if="serieJours.length" type="line" height="290" :options="optionsJours" :series="seriesJours" />
                    <div v-else class="mb-empty"><i class="ri-bar-chart-line"></i>Aucune vente sur la période.</div>
                </div>
            </div>
            <div class="mb-card">
                <div class="mb-card__head"><h2>Top 10 articles par bénéfice</h2><span>période</span></div>
                <div class="mb-card__body">
                    <apexchart v-if="top10.length" type="bar" height="290" :options="optionsTop" :series="seriesTop" />
                    <div v-else class="mb-empty"><i class="ri-bar-chart-line"></i>Aucune vente sur la période.</div>
                </div>
            </div>
            <div class="mb-card">
                <div class="mb-card__head"><h2>Bénéfice par magasin</h2><span>période</span></div>
                <div class="mb-card__body">
                    <apexchart v-if="parMagasin.length" type="donut" height="290" :options="optionsMagasins" :series="parMagasin.map(m => m.bn)" />
                    <div v-else class="mb-empty"><i class="ri-pie-chart-line"></i>Aucune vente sur la période.</div>
                </div>
            </div>
            <div class="mb-card">
                <div class="mb-card__head"><h2>Marge réelle par jour</h2><span>bénéfice / ventes</span></div>
                <div class="mb-card__body">
                    <apexchart v-if="serieJours.length" type="area" height="290" :options="optionsMarge" :series="seriesMarge" />
                    <div v-else class="mb-empty"><i class="ri-line-chart-line"></i>Aucune vente sur la période.</div>
                </div>
            </div>
        </div>

    </div>

</template>

<script>
    import VueApexCharts from "vue3-apexcharts";
    import {currencyFormatDE, currentDateTime, ExportExcel, randomString} from "@/allfunction";
    import * as JwtService from "@/common/jwt.service";
    const axios =require('axios');
    import { avecCacheTB } from "@/service/cacheTableauDeBord";
    import bandeaucachetb from "@/components/views/pages/tableaudebord/bandeaucachetb";

    const COULEURS = ['#7c5ae0','#2563eb','#059669','#d97706','#db2777','#0891b2','#65a30d','#9333ea'];

    /* Date locale AAAA-MM-JJ (pas toISOString, qui passe en UTC). */
    const isoLocal = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

    export default {
        /* eslint-disable */
        name: 'MargeBeneficesDashboard',

        components: {
            bandeaucachetb,
            apexchart: VueApexCharts
        },

        data() {
            return {
                depuisCache: false,
                heureCache: '',
                chargement: false,
                charge: false,
                derniereMaj: '',

                /* lignes brutes renvoyées par l'API (une par jour x magasin x article x prix x type) */
                lignes: [],

                presets: [
                    { code: '7', label: '7 jours' },
                    { code: '30', label: '30 jours' },
                    { code: 'mois', label: 'Ce mois' },
                    { code: 'moisprec', label: 'Mois dernier' },
                    { code: '90', label: '3 mois' },
                    { code: 'perso', label: 'Dates' },
                ],
                preset: '7',
                dateDebut: '',
                dateFin: '',
                magasin: '',

                vue: 'table',
                recherche: '',
                regroupements: [
                    { code: 'article', label: 'Par article' },
                    { code: 'artmag', label: 'Article + magasin' },
                    { code: 'jour', label: 'Détail par jour' },
                ],
                regroupement: 'article',
                pastilles: [
                    { code: 'tous', label: 'Tous', cls: '' },
                    { code: 'top', label: 'Marge ≥ 40 %', cls: '' },
                    { code: 'neg', label: 'Bénéfice négatif', cls: 'mb-chip--bad' },
                    { code: 'sanspa', label: "Sans prix d'achat", cls: 'mb-chip--warn' },
                    { code: 'ret', label: 'Avec retours', cls: 'mb-chip--bad' },
                ],
                pastille: 'tous',
                colonnes: [
                    { k: 'art', l: 'Article' },
                    { k: 'q', l: 'Qté', r: 1 },
                    { k: 'pa', l: 'Prix achat', r: 1, g: 'a' },
                    { k: 'ma', l: 'Montant achat', r: 1, g: 'a' },
                    { k: 'pu', l: 'Prix vente', r: 1, g: 'v' },
                    { k: 'mv', l: 'Montant vente', r: 1, g: 'v' },
                    { k: 'bn', l: 'Bénéfice', r: 1, g: 'b' },
                    { k: 'marge', l: 'Marge', r: 1, g: 'b' },
                ],
                tri: 'bn',
                sens: -1,
                page: 1,
                parPage: 25,
            }
        },

        computed: {

            magasins() {
                return [...new Set(this.lignes.map(l => l.mag))].sort();
            },

            /* Lignes de la période pour le magasin choisi. */
            lignesMagasin() {
                return this.magasin ? this.lignes.filter(l => l.mag === this.magasin) : this.lignes;
            },

            /* Regroupement selon le mode choisi. */
            groupes() {
                const m = {};
                const mode = this.regroupement;
                for (const l of this.lignesMagasin) {
                    const k = mode === 'article' ? l.art
                        : mode === 'artmag' ? l.art + '|' + l.mag
                        : l.j + '|' + l.art + '|' + l.mag + '|' + l.pu;
                    let g = m[k];
                    if (!g) {
                        g = m[k] = { cle: k, art: l.art, mag: mode === 'article' ? null : l.mag, j: mode === 'jour' ? l.j : null,
                            q: 0, ma: 0, mv: 0, rem: 0, mags: {}, br: 0, sanspa: false };
                    }
                    g.q += l.q; g.ma += l.ma; g.mv += l.mv; g.rem += l.rem;
                    g.mags[l.mag] = 1;
                    if (l.br) g.br++;
                    if (l.pa === 0) g.sanspa = true;
                }
                return Object.values(m).map(g => {
                    g.bn = g.mv - g.ma;
                    g.pa = g.q ? g.ma / g.q : 0;
                    g.pu = g.q ? (g.mv + g.rem) / g.q : 0;
                    g.marge = g.mv ? g.bn / g.mv * 100 : 0;
                    g.nmag = Object.keys(g.mags).length;
                    return g;
                });
            },

            groupesRecherche() {
                const q = this.recherche.trim().toLowerCase();
                if (!q) return this.groupes;
                return this.groupes.filter(g => (g.art + ' ' + (g.mag || Object.keys(g.mags).join(' '))).toLowerCase().includes(q));
            },

            compteurs() {
                const c = {};
                for (const p of this.pastilles) c[p.code] = this.groupesRecherche.filter(g => this.passePastille(g, p.code)).length;
                return c;
            },

            lignesFiltrees() {
                const rows = this.groupesRecherche.filter(g => this.passePastille(g, this.pastille));
                const k = this.tri, s = this.sens;
                rows.sort((a, b) => {
                    if (k === 'art') return s * a.art.localeCompare(b.art) || (b.j || '').localeCompare(a.j || '');
                    return s * (a[k] - b[k]);
                });
                return rows;
            },

            nbPages() {
                return Math.max(1, Math.ceil(this.lignesFiltrees.length / this.parPage));
            },

            lignesPage() {
                const p = Math.min(this.page, this.nbPages);
                return this.lignesFiltrees.slice((p - 1) * this.parPage, p * this.parPage);
            },

            pagesVisibles() {
                let st = Math.max(1, this.page - 2);
                const en = Math.min(this.nbPages, st + 4);
                st = Math.max(1, en - 4);
                const r = [];
                for (let i = st; i <= en; i++) r.push(i);
                return r;
            },

            /* KPI : période + magasin, sans la recherche ni les pastilles. */
            totaux() {
                const t = { q: 0, ma: 0, mv: 0, bn: 0, sanspa: 0, ventesSansPa: 0, marge: 0 };
                for (const l of this.lignesMagasin) {
                    t.q += l.q; t.ma += l.ma; t.mv += l.mv;
                    if (l.pa === 0) t.ventesSansPa += l.mv;
                }
                t.bn = t.mv - t.ma;
                t.marge = t.mv ? t.bn / t.mv * 100 : 0;
                t.sanspa = new Set(this.lignesMagasin.filter(l => l.pa === 0).map(l => l.art)).size;
                return t;
            },

            /* Totaux de toute la sélection filtrée (pas seulement la page affichée). */
            totauxSelection() {
                const t = this.lignesFiltrees.reduce((a, g) => { a.q += g.q; a.ma += g.ma; a.mv += g.mv; a.bn += g.bn; return a; }, { q: 0, ma: 0, mv: 0, bn: 0 });
                t.marge = t.mv ? t.bn / t.mv * 100 : 0;
                return t;
            },

            /* ---------- graphiques ---------- */
            serieJours() {
                const d = {};
                for (const l of this.lignesMagasin) {
                    const x = d[l.j] || (d[l.j] = { j: l.j, mv: 0, ma: 0 });
                    x.mv += l.mv; x.ma += l.ma;
                }
                return Object.values(d).sort((a, b) => a.j.localeCompare(b.j));
            },
            seriesJours() {
                return [
                    { name: 'Ventes', type: 'column', data: this.serieJours.map(x => Math.round(x.mv)) },
                    { name: 'Achats', type: 'column', data: this.serieJours.map(x => Math.round(x.ma)) },
                    { name: 'Bénéfice', type: 'line', data: this.serieJours.map(x => Math.round(x.mv - x.ma)) },
                ];
            },
            optionsJours() {
                return Object.assign(this.optionsBase(), {
                    colors: ['#7c5ae0', '#f59e0b', '#059669'],
                    stroke: { width: [0, 0, 2] },
                    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
                    xaxis: this.axeJours(),
                    yaxis: { labels: { formatter: v => this.abrege(v), style: { colors: '#94a3b8' } } },
                    legend: { position: 'top', horizontalAlign: 'right' },
                });
            },
            seriesMarge() {
                return [{ name: 'Marge réelle', data: this.serieJours.map(x => x.mv ? +((x.mv - x.ma) / x.mv * 100).toFixed(1) : 0) }];
            },
            optionsMarge() {
                return Object.assign(this.optionsBase(), {
                    colors: ['#2563eb'],
                    stroke: { curve: 'smooth', width: 2 },
                    fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
                    xaxis: this.axeJours(),
                    yaxis: { labels: { formatter: v => Math.round(v) + ' %', style: { colors: '#94a3b8' } } },
                    tooltip: { y: { formatter: v => this.formatPourcent(v) + ' %' } },
                });
            },
            top10() {
                const m = {};
                for (const l of this.lignesMagasin) m[l.art] = (m[l.art] || 0) + (l.mv - l.ma);
                return Object.entries(m).map(([art, bn]) => ({ art, bn })).sort((a, b) => b.bn - a.bn).slice(0, 10);
            },
            seriesTop() {
                return [{ name: 'Bénéfice', data: this.top10.map(x => Math.round(x.bn)) }];
            },
            optionsTop() {
                return Object.assign(this.optionsBase(), {
                    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    colors: ['#059669'],
                    plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '65%' } },
                    xaxis: {
                        categories: this.top10.map(x => x.art.length > 26 ? x.art.slice(0, 26) + '…' : x.art),
                        labels: { formatter: v => this.abrege(v), style: { colors: '#94a3b8' } },
                    },
                });
            },
            parMagasin() {
                const m = {};
                for (const l of this.lignesMagasin) m[l.mag] = (m[l.mag] || 0) + (l.mv - l.ma);
                return Object.entries(m).map(([mag, bn]) => ({ mag, bn: Math.round(bn) })).filter(x => x.bn > 0);
            },
            optionsMagasins() {
                const t = this;
                return Object.assign(this.optionsBase(), {
                    chart: { type: 'donut', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    labels: this.parMagasin.map(x => x.mag),
                    colors: COULEURS,
                    legend: { position: 'bottom' },
                    dataLabels: { enabled: true, formatter: v => v.toFixed(0) + ' %' },
                    plotOptions: { pie: { donut: { size: '62%', labels: { show: true, total: { show: true, label: 'Bénéfice',
                        formatter: w => t.formatMoney(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) } } } } },
                });
            },
        },

        methods: {

            /* ---------- chargement ---------- */
            async charger(force) {
                if (this.chargement) return;
                const t = this;
                this.chargement = true;

                const baseUrl = process.env.VUE_APP_API_URL + '/egl745874tb771714dfe/dash/graph/587999fff9ddbenef784517788/custom/' + this.$store.state.appsociete + '/' + randomString();
                const queryParams = new URLSearchParams({
                    typeperiode: 'custom',
                    datedebut: this.dateDebut,
                    datefin: this.dateFin,
                    _: Date.now().toString()
                });

                try {
                    const r = await avecCacheTB(this.$store.state.appsociete, 'marge', { dateDebut: this.dateDebut, dateFin: this.dateFin }, async () => {
                        const response = await axios.get(baseUrl + '?' + queryParams.toString(), {
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + JwtService.getToken(),
                                'csrftoken': this.$store.state.appsociete
                            },
                            withCredentials: true // Permet l'envoi de cookies et autres informations d'authentification
                        });
                        return response.data.success ? (response.data.dm || []) : null;
                    }, { force: force === true });

                    if (Array.isArray(r.data)) {
                        /* Une ligne = jour x magasin x article x prix x type (F ou BR).
                           Les retours (BR) sont signés en négatif ; la remise est déduite des ventes,
                           comme dans le calcul du bénéfice côté serveur. */
                        t.lignes = r.data.map(item => {
                            const br = item.document !== 'Facture (Vente)';
                            const qte = (parseFloat(item.qtitetotal) || 0) * (br ? -1 : 1);
                            const pa = parseFloat(item.prixachat) || 0;
                            const pu = parseFloat(item.pu) || 0;
                            const rem = parseFloat(item.remisetotal) || 0;
                            return {
                                j: String(item.datecreated || '').slice(0, 10),
                                mag: item.nommaga || '(sans magasin)',
                                art: String(item.namearticle || '(sans libellé)').trim(),
                                pa, pu, rem, br,
                                q: qte,
                                ma: pa * qte,
                                mv: pu * qte - rem,
                            };
                        });
                        if (t.magasin && !t.magasins.includes(t.magasin)) t.magasin = '';
                    } else {
                        t.lignes = [];
                    }
                    t.charge = true;
                    t.page = 1;
                    t.depuisCache = r.cache;
                    t.heureCache = String(new Date(r.t).getHours()).padStart(2, '0') + ':' + String(new Date(r.t).getMinutes()).padStart(2, '0');
                    const h = new Date(r.t);
                    t.derniereMaj = String(h.getHours()).padStart(2, '0') + ':' + String(h.getMinutes()).padStart(2, '0');
                } catch (e) {
                    toastr['error']("Une erreur est subvenue. <br/> veuillez actualiser la page.", "<b></b>");
                } finally {
                    t.chargement = false;
                }
            },

            loadinitcomponnent() {
                if (!this.charge && !this.chargement) this.choisirPreset(this.preset);
            },

            choisirPreset(code) {
                this.preset = code;
                const now = new Date();
                let d1 = new Date(now), d2 = new Date(now);
                if (code === '7') d1.setDate(d1.getDate() - 6);
                if (code === '30') d1.setDate(d1.getDate() - 29);
                if (code === '90') d1.setDate(d1.getDate() - 89);
                if (code === 'mois') d1 = new Date(now.getFullYear(), now.getMonth(), 1);
                if (code === 'moisprec') {
                    d1 = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    d2 = new Date(now.getFullYear(), now.getMonth(), 0);
                }
                if (code === 'perso') {
                    if (!this.dateDebut || !this.dateFin) { this.dateDebut = isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)); this.dateFin = isoLocal(now); }
                    this.charger();
                    return;
                }
                this.dateDebut = isoLocal(d1);
                this.dateFin = isoLocal(d2);
                this.charger();
            },

            /* ---------- tableau ---------- */
            passePastille(g, code) {
                if (code === 'neg') return g.bn < 0;
                if (code === 'sanspa') return g.sanspa;
                if (code === 'ret') return g.br > 0;
                if (code === 'top') return g.marge >= 40;
                return true;
            },

            choisirPastille(code) {
                this.pastille = code;
                this.vue = 'table';
                this.page = 1;
            },

            trier(k) {
                if (this.tri === k) this.sens = -this.sens;
                else { this.tri = k; this.sens = k === 'art' ? 1 : -1; }
                this.page = 1;
            },

            meta(g) {
                const lieu = this.regroupement === 'article'
                    ? (g.nmag > 1 ? g.nmag + ' magasins' : Object.keys(g.mags)[0])
                    : g.mag;
                return lieu + (g.j ? ' · ' + this.formatDate(g.j) : '');
            },

            echapper(s) {
                return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
            },

            /* Surligne la recherche (texte échappé avant insertion). */
            surligner(texte) {
                const s = String(texte || '');
                const q = this.recherche.trim();
                if (!q) return this.echapper(s);
                const i = s.toLowerCase().indexOf(q.toLowerCase());
                if (i < 0) return this.echapper(s);
                return this.echapper(s.slice(0, i)) + '<mark>' + this.echapper(s.slice(i, i + q.length)) + '</mark>' + this.echapper(s.slice(i + q.length));
            },

            couleur(nom) {
                let h = 0;
                for (let i = 0; i < nom.length; i++) h = (h * 31 + nom.charCodeAt(i)) >>> 0;
                return COULEURS[h % COULEURS.length];
            },

            initiales(nom) {
                return nom.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
            },

            couleurMarge(m) {
                return m < 0 ? '#dc2626' : m < 15 ? '#d97706' : m < 40 ? '#2563eb' : '#059669';
            },

            exporterExcel() {
                ExportExcel('xlsx', 'marges_' + this.dateDebut + '_' + this.dateFin + '_' + currentDateTime(), undefined, undefined, this.$refs.exportable_table);
            },

            /* ---------- graphiques ---------- */
            optionsBase() {
                return {
                    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit', animations: { enabled: false } },
                    dataLabels: { enabled: false },
                    grid: { borderColor: '#e2e8f0', strokeDashArray: 3 },
                    tooltip: { shared: true, intersect: false, y: { formatter: v => this.formatMoney(v) + ' F' } },
                };
            },

            axeJours() {
                return {
                    categories: this.serieJours.map(x => x.j.slice(8, 10) + '/' + x.j.slice(5, 7)),
                    tickAmount: this.serieJours.length > 16 ? 10 : undefined,
                    labels: { rotate: -45, hideOverlappingLabels: true, style: { colors: '#94a3b8', fontSize: '11px' } },
                    axisTicks: { show: false },
                };
            },

            /* ---------- formats ---------- */
            formatMoney(v) {
                return currencyFormatDE(Math.round(Number(v) || 0));
            },

            /* Quantités et compteurs : séparateur de milliers, sans devise. */
            formatNombre(v) {
                return (Number(v) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 }).replace(/[  ]/g, ' ');
            },

            formatPourcent(v) {
                return (Number(v) || 0).toFixed(1).replace('.', ',');
            },

            arrondi(v) {
                return Math.round((Number(v) || 0) * 100) / 100;
            },

            abrege(v) {
                const a = Math.abs(v);
                if (a >= 1e6) return (v / 1e6).toFixed(1).replace('.0', '') + ' M';
                if (a >= 1e3) return Math.round(v / 1e3) + ' k';
                return Math.round(v);
            },

            formatDate(j) {
                if (!j) return '';
                const s = String(j).slice(0, 10).split('-');
                return s.length === 3 ? s[2] + '/' + s[1] + '/' + s[0] : j;
            },

            raccourciRecherche(e) {
                const tag = (e.target && e.target.tagName) || '';
                if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) && this.$el.offsetParent !== null && this.vue === 'table') {
                    e.preventDefault();
                    this.$refs.recherche && this.$refs.recherche.focus();
                }
            },
        },

        watch: {
            recherche() { this.page = 1; },
            magasin() { this.page = 1; },
        },

        mounted() {
            document.addEventListener('keydown', this.raccourciRecherche);
        },

        beforeUnmount() {
            document.removeEventListener('keydown', this.raccourciRecherche);
        },
    }
</script>

<style scoped>

.mb-page { padding-bottom: 1rem; }

/* filtres */
.mb-filters { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
.mb-seg { display: inline-flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 3px; gap: 2px; }
.mb-seg button { border: 0; background: transparent; font-size: 0.8rem; font-weight: 500; padding: 0.3rem 0.7rem; border-radius: var(--cf-radius-xs); color: var(--cf-muted); cursor: pointer; white-space: nowrap; }
.mb-seg button:hover { color: rgb(var(--primary)); }
.mb-seg button.mb-seg--on { background: rgb(var(--primary)); color: #fff; }
.mb-input { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 0.38rem 0.6rem; font-size: 0.8rem; color: var(--cf-ink); max-width: 100%; }
.mb-btn { height: 2.1rem; padding: 0 0.7rem; display: inline-flex; gap: 0.35rem; align-items: center; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); color: var(--cf-muted); cursor: pointer; font-size: 0.8rem; font-weight: 500; }
.mb-btn:hover:not(:disabled) { color: rgb(var(--primary)); border-color: rgb(var(--primary) / 0.4); }
.mb-btn:disabled { opacity: 0.5; cursor: default; }
.mb-btn--xls { color: #047857; }
.mb-spin { display: inline-block; animation: mb-rot 0.9s linear infinite; }
@keyframes mb-rot { to { transform: rotate(360deg); } }

/* KPI */
.mb-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
@media (min-width: 1100px) { .mb-kpis { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
.mb-kpi { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); padding: 0.9rem 1rem; min-width: 0; transition: box-shadow 0.15s, transform 0.15s; }
.mb-kpi:hover { box-shadow: var(--cf-shadow-md); transform: translateY(-1px); }
.mb-kpi__lbl { display: flex; justify-content: space-between; align-items: center; color: var(--cf-muted); font-size: 0.78rem; font-weight: 500; }
.mb-kpi__lbl i { width: 1.9rem; height: 1.9rem; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 1rem; }
.mb-kpi__val { font-size: 1.4rem; font-weight: 800; color: var(--cf-ink-strong); margin: 0.35rem 0 0.1rem; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.mb-kpi__val small { font-size: 0.75rem; font-weight: 500; color: var(--cf-muted); }
.mb-kpi__sub { font-size: 0.72rem; color: var(--cf-muted); }
.mb-kpi--main { background: linear-gradient(135deg, #059669 0%, #047857 100%); border: 0; color: #fff; }
.mb-kpi--main .mb-kpi__lbl, .mb-kpi--main .mb-kpi__sub { color: rgb(255 255 255 / 0.85); }
.mb-kpi--main .mb-kpi__lbl i { background: rgb(255 255 255 / 0.18); }
.mb-kpi--main .mb-kpi__val, .mb-kpi--main .mb-kpi__val small { color: #fff; }
.mb-kpi--warn { border-left: 4px solid #d97706; cursor: pointer; }
.mb-ico-vente { background: #f1ecfd; color: #7c5ae0; }
.mb-ico-achat { background: #fff7e6; color: #d97706; }
.mb-ico-marge { background: #eff6ff; color: #2563eb; }
.mb-ico-warn { background: #fff7ed; color: #d97706; }
.mb-warn { color: #d97706; }
.mb-gauge { height: 6px; background: var(--cf-surface-alt); border-radius: 99px; overflow: hidden; margin-top: 0.45rem; }
.mb-gauge i { display: block; height: 100%; background: #2563eb; border-radius: 99px; }

/* vues */
.mb-views { display: flex; gap: 0.25rem; border-bottom: 1px solid var(--cf-border); margin-bottom: 1rem; }
.mb-views button { border: 0; background: none; font-size: 0.85rem; font-weight: 600; color: var(--cf-muted); padding: 0.6rem 1rem; border-bottom: 2px solid transparent; cursor: pointer; display: flex; gap: 0.4rem; align-items: center; }
.mb-views button.mb-views--on { color: rgb(var(--primary)); border-bottom-color: rgb(var(--primary)); }

/* carte */
.mb-card { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); min-width: 0; }
.mb-card__head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; border-bottom: 1px solid var(--cf-border); }
.mb-card__head h2 { margin: 0; font-size: 0.9rem; font-weight: 700; color: var(--cf-ink-strong); }
.mb-card__head span { font-size: 0.75rem; color: var(--cf-muted); }
.mb-card__body { padding: 0.75rem 1rem; }

/* barre d'outils */
.mb-toolbar { display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: center; padding: 0.85rem 1rem; border-bottom: 1px solid var(--cf-border); }
.mb-search { position: relative; flex: 1 1 280px; min-width: 200px; }
.mb-search > i { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--cf-faint); font-size: 1rem; }
.mb-search input { width: 100%; padding: 0.55rem 2.2rem 0.55rem 2.2rem; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); font-size: 0.85rem; background: var(--cf-surface); }
.mb-search input:focus { outline: none; border-color: rgb(var(--primary)); background: #fff; box-shadow: 0 0 0 3px rgb(var(--primary) / 0.12); }
.mb-search kbd { position: absolute; right: 0.6rem; top: 50%; transform: translateY(-50%); font-size: 0.68rem; color: var(--cf-faint); border: 1px solid var(--cf-border); border-radius: 4px; padding: 0 5px; background: #fff; }
.mb-search__clear { position: absolute; right: 0.5rem; top: 50%; transform: translateY(-50%); border: 0; background: var(--cf-surface-alt); border-radius: 99px; width: 1.4rem; height: 1.4rem; cursor: pointer; color: var(--cf-muted); display: inline-flex; align-items: center; justify-content: center; }

/* pastilles */
.mb-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; padding: 0.6rem 1rem; border-bottom: 1px solid var(--cf-border); background: var(--cf-surface); }
.mb-chip { border: 1px solid var(--cf-border); background: #fff; border-radius: 999px; padding: 0.25rem 0.7rem; font-size: 0.76rem; font-weight: 500; color: var(--cf-muted); cursor: pointer; display: inline-flex; gap: 0.35rem; align-items: center; }
.mb-chip b { background: var(--cf-surface-alt); color: var(--cf-ink); border-radius: 999px; padding: 0 0.45rem; font-size: 0.7rem; }
.mb-chip--bad { color: #dc2626; }
.mb-chip--warn { color: #d97706; }
.mb-chip.mb-chip--on { background: rgb(var(--primary)); border-color: rgb(var(--primary)); color: #fff; }
.mb-chip.mb-chip--on b { background: rgb(255 255 255 / 0.25); color: #fff; }
.mb-chips__sep { flex: 1; }
.mb-resume { font-size: 0.76rem; color: var(--cf-muted); align-self: center; }

/* tableau */
.mb-table-wrap { overflow: auto; max-height: 620px; }
.mb-table-wrap table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.82rem; }
.th { position: sticky; top: 0; z-index: 2; padding: 0.65rem 0.9rem; font-weight: 600; font-size: 0.72rem; color: var(--cf-ink); background: var(--cf-surface-alt); border-bottom: 1px solid var(--cf-border); white-space: nowrap; text-align: left; cursor: pointer; user-select: none; }
.th:hover { color: rgb(var(--primary)); }
.th--right { text-align: right; }
.th__s { opacity: 0.35; margin-left: 2px; }
.th--sorted .th__s { opacity: 1; color: rgb(var(--primary)); }
.th--a { border-top: 3px solid #f59e0b; }
.th--v { border-top: 3px solid #7c5ae0; }
.th--b { border-top: 3px solid #059669; }
.td { padding: 0.6rem 0.9rem; border-bottom: 1px solid var(--cf-surface-alt); white-space: nowrap; font-variant-numeric: tabular-nums; vertical-align: middle; color: var(--cf-ink); }
.td--right { text-align: right; }
.mb-row:hover .td { background: #faf8ff; }
.mb-ca { background: #fffcf5; }
.mb-cv { background: #faf8ff; }
.mb-art { display: flex; gap: 0.6rem; align-items: center; min-width: 220px; white-space: normal; }
.mb-avatar { flex: 0 0 auto; width: 2rem; height: 2rem; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.72rem; color: #fff; }
.mb-art__name { font-weight: 600; color: var(--cf-ink-strong); line-height: 1.2; }
.mb-art__meta { font-size: 0.7rem; color: var(--cf-muted); }
.mb-art :deep(mark), .mb-mcard :deep(mark) { background: #fef08a; color: inherit; border-radius: 2px; padding: 0 1px; }
.mb-tag { display: inline-block; font-size: 0.64rem; font-weight: 700; padding: 1px 6px; border-radius: 999px; margin-left: 4px; vertical-align: middle; }
.mb-tag--ret { background: #fef2f2; color: #dc2626; }
.mb-tag--pa { background: #fff7ed; color: #d97706; }
.mb-neg { color: #dc2626; font-weight: 600; }
.mb-pos { color: #059669; font-weight: 600; }
.mb-faint { color: var(--cf-faint); }
.mb-mbar { display: inline-flex; align-items: center; gap: 0.45rem; justify-content: flex-end; }
.mb-mbar span { width: 46px; height: 6px; border-radius: 99px; background: var(--cf-surface-alt); overflow: hidden; display: inline-block; }
.mb-mbar span i { display: block; height: 100%; border-radius: 99px; }
tfoot .td { position: sticky; bottom: 0; z-index: 2; background: #1e293b; color: #fff; font-weight: 700; border-bottom: 0; }
.mb-foot__lbl { font-size: 0.68rem; font-weight: 500; color: #cbd5e1; display: block; }

/* pagination */
.mb-pager { display: flex; flex-wrap: wrap; gap: 0.5rem; justify-content: space-between; align-items: center; padding: 0.7rem 1rem; border-top: 1px solid var(--cf-border); font-size: 0.78rem; color: var(--cf-muted); }
.mb-pager__pg { display: flex; gap: 0.25rem; align-items: center; }
.mb-pager__pg button { min-width: 2rem; height: 2rem; border: 1px solid var(--cf-border); background: #fff; border-radius: var(--cf-radius-xs); cursor: pointer; font-size: 0.78rem; color: var(--cf-ink); }
.mb-pager__pg button.mb-pager--on { background: rgb(var(--primary)); border-color: rgb(var(--primary)); color: #fff; }
.mb-pager__pg button:disabled { opacity: 0.4; cursor: default; }

/* états */
.mb-empty { text-align: center; padding: 2.5rem 1rem; color: var(--cf-muted); font-size: 0.85rem; }
.mb-empty i { font-size: 2rem; color: var(--cf-faint); display: block; margin-bottom: 0.4rem; }
.mb-skel { height: 0.75rem; border-radius: 6px; margin: 0.55rem 0; background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%); background-size: 200% 100%; animation: mb-sh 1.3s infinite; }
.mb-skel--lg { height: 1.4rem; }
@keyframes mb-sh { to { background-position: -200% 0; } }
.mb-hidden { display: none; }

/* graphiques */
.mb-charts { display: grid; gap: 1rem; }
@media (min-width: 1024px) { .mb-charts { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); } }

/* mobile : lignes en cartes */
.mb-only-mobile { display: none; }
.mb-mcards { gap: 0.6rem; padding: 0.75rem; }
.mb-mcard { border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 0.75rem; }
.mb-mcard__g { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.45rem 0.8rem; margin-top: 0.6rem; font-size: 0.72rem; color: var(--cf-muted); }
.mb-mcard__g b { display: block; color: var(--cf-ink-strong); font-size: 0.82rem; font-variant-numeric: tabular-nums; }
.mb-mtot { border-radius: var(--cf-radius-sm); background: #1e293b; color: #fff; padding: 0.75rem; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.4rem; font-size: 0.75rem; }
.mb-mtot b { display: block; font-size: 0.9rem; }
@media (max-width: 760px) {
    .mb-only-desktop { display: none; }
    .mb-only-mobile { display: grid; }
    .mb-kpi__val { font-size: 1.1rem; }
}

</style>
